import { createUseStyles } from "react-jss";
import { colors } from "../../styles/colors";
import { Z } from "../../styles/zIndex";

/*
 * Layout note.
 *
 * This screen is a workspace, not a document: the map and the vehicle list are
 * read together and neither should scroll the other away. The page therefore
 * fills the viewport exactly once and only the list scrolls internally.
 *
 * The previous version stacked header, a search card, a 500px map and then the
 * vehicle list, so the list lived below the fold and reaching it pushed the map
 * off screen — on a tracking page, where the answer is the map plus the list
 * together.
 */
const useStyles = createUseStyles({
  contentMain: {
    overflow: "hidden",
    height: "var(--app-vh)",
  },

  geolocationRoot: {
    padding: "20px clamp(16px, 2.5vw, 28px)",
    height: "var(--app-vh)",
    // The page itself does not scroll; the vehicle rail does.
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    backgroundColor: colors.bgcolor,
    "@media (max-width:899.95px)": {
      paddingTop: "68px",
      // Below the split layout the two panes stack, so the page scrolls again.
      overflowY: "auto",
    },
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    flexShrink: 0,
    "@media (max-width:899.95px)": {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: "12px",
    },
  },

  headerTitleSection: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    minWidth: 0,
  },

  headerButtons: {
    display: "flex",
    gap: "10px",
    flexShrink: 0,
  },

  connectionStatus: {
    display: "flex",
    alignItems: "center",
    gap: "7px",
  },

  connectionDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    flexShrink: 0,
    transition: "background-color 0.3s ease",
  },

  connected: { backgroundColor: "#22c55e" },
  disconnected: { backgroundColor: "#ef4444" },

  alertsContainer: {
    padding: "10px 14px",
    borderRadius: "8px",
    backgroundColor: colors.surfaceElevated,
    border: `1px solid ${colors.borderLight}`,
    flexShrink: 0,
  },

  // ── Split workspace ──────────────────────────────────────────────────
  workspace: {
    flex: 1,
    minHeight: 0, // lets the children scroll instead of stretching the page
    display: "grid",
    gridTemplateColumns: "340px 1fr",
    gap: "14px",
    "@media (max-width: 1100px)": {
      gridTemplateColumns: "300px 1fr",
    },
    "@media (max-width: 899.95px)": {
      gridTemplateColumns: "1fr",
      // Explicit heights on mobile: an auto row inside a stacked grid gives
      // the map zero height and it renders blank.
      gridTemplateRows: "auto 60vh",
    },
  },

  rail: {
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    backgroundColor: colors.surface,
    border: `1px solid ${colors.borderLight}`,
    borderRadius: "12px",
    overflow: "hidden",
  },

  railSearch: {
    padding: "12px",
    borderBottom: `1px solid ${colors.borderLight}`,
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    flexShrink: 0,
  },

  searchInput: {
    width: "100%",
    padding: "9px 12px",
    borderRadius: "8px",
    border: `1px solid ${colors.borderLight}`,
    backgroundColor: colors.surfaceElevated,
    color: colors.darkblack,
    fontSize: "13.5px",
    outline: "none",
    "&:focus": { borderColor: colors.primary },
    "&::placeholder": { color: "#9ca3af" },
  },

  filterRow: {
    display: "flex",
    gap: "6px",
  },

  /*
   * The legend became the filter.
   *
   * Two colour swatches labelled Actif/Inactif used to sit next to the search
   * box purely as a key. Making them the control costs no extra space and
   * turns a caption into the fastest way to answer "what is not moving?".
   */
  filterChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "5px 9px",
    borderRadius: "999px",
    border: `1px solid ${colors.borderLight}`,
    backgroundColor: "transparent",
    color: colors.darkblack,
    fontSize: "12px",
    fontWeight: 500,
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "border-color .15s, background-color .15s",
    "&:hover": { borderColor: colors.primary },
  },

  filterChipActive: {
    borderColor: colors.primary,
    backgroundColor: colors.surfaceElevated,
    fontWeight: 700,
  },

  filterCount: {
    opacity: 0.65,
    fontVariantNumeric: "tabular-nums",
  },

  legendDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    flexShrink: 0,
  },

  railList: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
  },

  /*
   * Rows, not cards.
   *
   * The list was a grid of 280px cards, which fits four vehicles on a screen.
   * A fleet list is scanned, so density wins: a row shows the same registration,
   * model, status and speed in a third of the height.
   */
  vehicleRow: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 12px",
    border: "none",
    borderBottom: `1px solid ${colors.borderLight}`,
    borderLeft: "3px solid transparent",
    backgroundColor: "transparent",
    cursor: "pointer",
    textAlign: "left",
    color: colors.darkblack,
    "&:hover": { backgroundColor: colors.surfaceElevated },
  },

  vehicleRowSelected: {
    backgroundColor: colors.surfaceElevated,
    borderLeftColor: colors.primary,
  },

  vehicleStatusDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    flexShrink: 0,
  },

  vehicleRowBody: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    flex: 1,
  },

  vehicleRowTitle: {
    fontSize: "13.5px",
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  vehicleRowSub: {
    fontSize: "12px",
    color: "#9ca3af",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  vehicleRowSpeed: {
    fontSize: "12px",
    color: "#9ca3af",
    flexShrink: 0,
    fontVariantNumeric: "tabular-nums",
  },

  // ── Detail panel ─────────────────────────────────────────────────────
  detailPanel: {
    borderTop: `1px solid ${colors.borderLight}`,
    padding: "12px",
    backgroundColor: colors.surfaceElevated,
    flexShrink: 0,
    maxHeight: "45%",
    overflowY: "auto",
  },

  detailHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px",
  },

  detailClose: {
    border: "none",
    background: "transparent",
    color: "#9ca3af",
    fontSize: "20px",
    lineHeight: 1,
    cursor: "pointer",
    padding: "0 4px",
    "&:hover": { color: colors.darkblack },
  },

  /* A definition list, because that is what these pairs are — and it gives
     assistive tech the label/value relationship for free. */
  detailGrid: {
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    rowGap: "6px",
    columnGap: "12px",
    margin: 0,
  },

  detailKey: {
    fontSize: "12px",
    color: "#9ca3af",
  },

  detailValue: {
    fontSize: "12.5px",
    fontWeight: 600,
    textAlign: "right",
    margin: 0,
    color: colors.darkblack,
    fontVariantNumeric: "tabular-nums",
  },

  detailFooter: {
    marginTop: "10px",
    fontSize: "11px",
    color: "#9ca3af",
  },

  // ── Map ──────────────────────────────────────────────────────────────
  mapContainer: {
    position: "relative",
    minHeight: 0,
    borderRadius: "12px",
    overflow: "hidden",
    border: `1px solid ${colors.borderLight}`,
    backgroundColor: colors.surface,
  },

  mapElement: {
    width: "100%",
    height: "100%",
  },

  /* Sits over the map rather than replacing it: the basemap still gives
     context, and the message explains why no markers are on it. */
  mapEmptyOverlay: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    padding: "12px 18px",
    borderRadius: "10px",
    backgroundColor: colors.surface,
    border: `1px solid ${colors.borderLight}`,
    boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
    zIndex: Z.OVERLAY,
    textAlign: "center",
    pointerEvents: "none",
  },

  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    padding: "36px 20px",
    textAlign: "center",
  },
});

export default useStyles;
